import { useState, useEffect } from "react";
import { API } from "../App";
import axios from "axios";
import { Building2, Search } from "lucide-react";

const OrgSelector = ({ value, onChange, label = "Client Organization", required = false, showVendorId = false, vendorId = "", onVendorIdChange }) => {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/crm/organizations`, { withCredentials: true })
      .then(res => {
        const data = Array.isArray(res.data) ? res.data : res.data.organizations || [];
        setOrgs(data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const filtered = orgs.filter(o =>
    o.name?.toLowerCase().includes(search.toLowerCase()) ||
    o.org_id?.toLowerCase().includes(search.toLowerCase())
  );

  const selected = orgs.find(o => o.org_id === value);

  return (
    <div className="space-y-2">
      <div className="relative">
        <label className="text-[#8B95A5] text-xs mb-1 block">
          {label} {required && <span className="text-red-400">*</span>}
        </label>
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-sm text-left flex items-center justify-between gap-2"
          data-testid="org-selector-trigger"
        >
          {selected ? (
            <span className="text-white flex items-center gap-2">
              <Building2 size={14} className="text-[#38BDF8]" />
              {selected.name}
              <span className="text-[#64748B] text-xs">({selected.org_id})</span>
            </span>
          ) : (
            <span className="text-[#64748B]">
              {loading ? "Loading organizations..." : "Select organization"}
            </span>
          )}
          <svg className="w-4 h-4 text-[#64748B]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {open && (
          <div className="absolute z-50 w-full mt-1 bg-[#111318] border border-[rgba(255,255,255,0.15)] rounded-[3px] shadow-xl max-h-64 overflow-hidden" data-testid="org-selector-dropdown">
            <div className="p-2 border-b border-[rgba(255,255,255,0.1)]">
              <div className="flex items-center gap-2 px-2 py-1.5 bg-[#0A1628] rounded">
                <Search size={14} className="text-[#64748B]" />
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search by name or ID..."
                  className="bg-transparent text-white text-sm outline-none flex-1 placeholder-[#64748B]"
                  autoFocus
                  data-testid="org-search-input"
                />
              </div>
            </div>
            <div className="overflow-y-auto max-h-48">
              {value && (
                <button
                  onClick={() => { onChange("", ""); setOpen(false); setSearch(""); }}
                  className="w-full px-3 py-2 text-left text-xs text-[#F59E0B] hover:bg-[#1E3A5F]/30 border-b border-[rgba(255,255,255,0.05)]"
                >
                  Clear selection
                </button>
              )}
              {filtered.length === 0 ? (
                <p className="px-3 py-2 text-[#64748B] text-xs">No organizations found</p>
              ) : (
                filtered.map(org => (
                  <button
                    key={org.org_id}
                    onClick={() => { onChange(org.org_id, org.name); setOpen(false); setSearch(""); }}
                    className={`w-full px-3 py-2 text-left hover:bg-[#1E3A5F]/30 flex items-center justify-between ${value === org.org_id ? "bg-[#1E3A5F]/20" : ""}`}
                    data-testid={`org-option-${org.org_id}`}
                  >
                    <span className="text-white text-sm">{org.name}</span>
                    <span className="text-[#64748B] text-xs">{org.org_id}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {showVendorId && (
        <div>
          <label className="text-[#8B95A5] text-xs mb-1 block">Vendor / Supplier ID</label>
          <input
            value={vendorId}
            onChange={e => onVendorIdChange?.(e.target.value)}
            placeholder="Client-provided vendor ID"
            className="w-full px-3 py-2.5 bg-[#111318] border border-[rgba(255,255,255,0.1)] rounded-[3px] text-white text-sm outline-none placeholder-[#64748B]"
            data-testid="vendor-id-input"
          />
        </div>
      )}
    </div>
  );
};

export default OrgSelector;
